import React, {
  RefObject,
  useEffect,
} from 'react';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import uniqueId from 'lodash/uniqueId';
import {
  TemplateListItemDTO,
  TemplateFieldDTO,
} from 'dtos';
import Table from 'core/components/VirtualTable';
import {
  getFields,
} from 'views/EMR/Settings/transport';
import {
  useQueryClient,
} from '@tanstack/react-query';
import {
  templateKeys,
  templateListOptions,
  useTemplateQueryScope,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/templateQueries';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  baseTemplateColumns,
  sectionTemplateColumns,
  fieldColumns,
} from 'views/EMR/Settings/VisitNotes/columns';
import {
  VisitNoteParams,
} from 'views/EMR/Settings/VisitNotes/index';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  GetQueryParams,
} from 'pages/Dashboard/types/common';
import {
  useMedia,
  useSidePanel,
} from 'core/hooks';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  notEmptyString,
} from '@populateui/utils';

type Props = {
  type: VisitNoteParams['config'];
  tableRef: RefObject<VirtualTableInstance>;
  params: Partial<GetQueryParams>;
};

const tablePropsByType = {
  'master-templates': {
    emptySlot: 'No Master Templates Are Available',
    columns: baseTemplateColumns,
    dataKey: 'master-templates-data',
  },
  'section-templates': {
    emptySlot: 'No Templates Are Available',
    columns: sectionTemplateColumns,
    dataKey: 'section-templates-data',
  },
  fields: {
    emptySlot: 'No Fields Are Available',
    columns: fieldColumns,
    getData: getFields,
    dataKey: 'fields-settings-data',
  },
};

export default function VisitNoteConfigList({
  type,
  tableRef,
  params,
}: Props) {
  const { openPanel } = useSidePanel();
  const { requestMedia } = useMedia();
  const queryClient = useQueryClient();
  const scope = useTemplateQueryScope();
  const kind = type === 'master-templates' ? 'master' : 'section';
  const getTemplates = async ({ searchQuery, pagination, setTotalSize }: GetQueryParams) => {
    const response = await queryClient.fetchQuery(templateListOptions(scope, kind, {
      searchText: searchQuery,
      ...pagination,
    }));
    setTotalSize(response.pageProperties?.totalSize ?? 0);
    const data = response.data ?? [];
    const userIds = compact(data.map((item) => item.user?.userId));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
    return data;
  };

  const onPanelOpen = (data: TemplateListItemDTO = {}) => {
    const id = notEmptyString(data?.templateName)
      ? compact([type, data?.templateName, data?.sectionName]).join('-')
      : uniqueId('template__');

    openPanel(type === 'master-templates' ? 'masterTemplate' : 'sectionTemplate', {
      ...data,
      id,
    });
  };

  useEffect(() => {
    const unsubscribe = settingsEventChannel.on('onTemplateDetails', () => {
      onPanelOpen();
    });

    return () => { unsubscribe(); };
  }, [type]);

  useEffect(() => {
    const unsubscribe = settingsEventChannel.on('onVisitNoteSettingsChange', async (changedType, template) => {
      if (type === 'fields') {
        await tableRef.current?.refetch?.();
      }
      if (changedType === type && !isNil(template) && type !== 'fields') {
        onPanelOpen(template);
      }
    });

    return () => { unsubscribe(); };
  }, [type]);

  return (
    <Table<TemplateListItemDTO & TemplateFieldDTO>
      key={type}
      instanceRef={tableRef}
      {...tablePropsByType[type]}
      {...(type !== 'fields' ? {
        getData: getTemplates,
        dataKey: templateKeys.settings(scope, kind),
      } : {})}
      queryParams={{ ...params, requestMedia }}
      defaultPagination={{ pageSize: 25, pageIndex: 0 }}
      classes={{
        header: 'px-4',
        scrollContainer: 'justify-center items-center',
        outerRow: '!w-[calc(100%-32px)] left-4',
        row: '!items-center py-1',
      }}
      getRowId={(row) => row?.templateFieldId ?? row.templateName}
      onRowClick={(table, row) => {
        if (type === 'fields') {
          settingsEventChannel.emit('onTemplateFieldDialog', { data: row.original });
        } else {
          onPanelOpen(row.original);
        }
      }}
    />
  );
}
